import type { CategoryV2 } from "../services/CategoriesService";
import { getUserStoresForRetailer } from "../config/retailers";
import { normalizeStoreNumber } from "../utils/userUtils";
import type {
  WalmartStore,
  HomeDepotStore,
  DollarGeneralStore,
  CostcoStore,
} from "../utils/storeUtils";
import type { TargetStore } from "../hooks/useStores";
import type { User } from "../services/UserService";
import type { RetailerType } from "../config/retailers";

export const getValidUserStores = (input: {
  user: User | null;
  retailer: RetailerType;
  loadedStores?:
    | WalmartStore[]
    | HomeDepotStore[]
    | DollarGeneralStore[]
    | TargetStore[]
    | null;
}) => {
  const stores = getUserStoresForRetailer(input.user, input.retailer);
  if (!input.loadedStores) return stores;

  return stores.filter((storeId) => {
    if (input.retailer === "home_depot") {
      const normalizedStoreId = normalizeStoreNumber(storeId, input.retailer);
      return (input.loadedStores as HomeDepotStore[]).some(
        (store) => store.store_number === normalizedStoreId
      );
    }
    if (input.retailer === "target") {
      return (input.loadedStores as TargetStore[]).some(
        (store) => String(store["Store No."]) === storeId
      );
    }
    if (input.retailer === "dollar_general") {
      return (input.loadedStores as DollarGeneralStore[]).some(
        (store) => store.storeNumber === storeId
      );
    }
    return (input.loadedStores as WalmartStore[]).some(
      (store) => store.store_id === storeId
    );
  });
};

export const getStoreAddress = (input: {
  storeId: string;
  retailer: RetailerType;
  loadedStores?:
    | WalmartStore[]
    | HomeDepotStore[]
    | DollarGeneralStore[]
    | TargetStore[]
    | CostcoStore[]
    | null;
}) => {
  if (!input.loadedStores) return "";
  if (input.retailer === "home_depot") {
    const normalizedStoreId = normalizeStoreNumber(input.storeId, input.retailer);
    const paddedStoreId = normalizedStoreId.padStart(4, "0");
    const store = (input.loadedStores as HomeDepotStore[]).find(
      (s) => s.store_number === paddedStoreId
    );
    return store
      ? `${store.address_1}, ${store.city}, ${store.state} ${store.zip}`
      : "";
  }
  if (input.retailer === "target") {
    const store = (input.loadedStores as TargetStore[]).find(
      (s) => String(s["Store No."]) === input.storeId
    );
    return store
      ? `${store.Street}, ${store.City}, ${store.State} ${store.Zip_Code}`
      : "";
  }
  if (input.retailer === "dollar_general") {
    const store = (input.loadedStores as DollarGeneralStore[]).find(
      (s) => s.storeNumber === input.storeId
    );
    return store ? store.address : "";
  }
  if (input.retailer === "costco") {
    const num = parseInt(input.storeId.replace(/-wh$/, ""), 10);
    const store = (input.loadedStores as CostcoStore[]).find(
      (s) => s.warehouse_number === num
    );
    return store ? `${store.address}, ${store.location}` : "";
  }
  const store = (input.loadedStores as WalmartStore[]).find(
    (s) => s.store_id === input.storeId
  );
  return store ? store.address : "";
};

export const filterCategoriesBySearch = (
  categories: (CategoryV2 & { count?: number })[],
  searchTerm: string
) => {
  if (!searchTerm.trim()) return categories;
  const search = searchTerm.toLowerCase().trim();
  return categories.filter((cat) => cat.rootKey.toLowerCase().includes(search));
};

// Numeric root keys are internal subcategory identifiers, not choices exposed by
// the filter UI. Keeping one canonical selectable set prevents hidden categories
// from being sent when a user deselects a visible category.
export const getSelectableProductCategories = (
  categories: (CategoryV2 & { count?: number })[]
) => categories.filter((category) => !/\d/.test(category.rootKey));

export const NO_CATEGORIES = "__none__";

// Multi-select toggle. [] = every category selected, ["__none__"] = none.
// Reselecting the last unselected category collapses back to [] (all).
export const toggleProductCategorySelection = (
  selected: string[],
  rootKey: string,
  selectableIDs: string[]
): string[] => {
  const current = selected.length === 0 ? [...selectableIDs] : selected.filter((id) => id !== NO_CATEGORIES);
  const next = current.includes(rootKey) ? current.filter((id) => id !== rootKey) : [...current, rootKey];
  if (next.length === 0) return [NO_CATEGORIES];
  return selectableIDs.every((id) => next.includes(id)) ? [] : next;
};

export const isProductCategorySelected = (selected: string[], rootKey: string) =>
  selected.length === 0 || selected.includes(rootKey);
